import { useEffect, useMemo, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  FILE_INTAKE_ACTION_MODE_OPTIONS,
  FILE_INTAKE_PURPOSE_OPTIONS,
  purposeRequiresSite,
  type FileIntakeActionMode,
  type FileIntakePurpose,
} from '@/lib/file-intake-dsl';

export interface FilePurposeDialogValue {
  purpose: FileIntakePurpose;
  instruction: string;
  site: string;
  autoActionMode: FileIntakeActionMode;
  autoIndexKnowledge: boolean;
}

interface FilePurposeDialogProps {
  open: boolean;
  files: File[];
  loading?: boolean;
  defaultPurpose?: FileIntakePurpose;
  defaultActionMode?: FileIntakeActionMode;
  title?: string;
  onCancel: () => void;
  onConfirm: (value: FilePurposeDialogValue) => void;
}

export function FilePurposeDialog(props: FilePurposeDialogProps) {
  const {
    open,
    files,
    loading = false,
    defaultPurpose = 'attach_as_evidence',
    defaultActionMode = 'suggest',
    title = 'What should we do with these files?',
    onCancel,
    onConfirm,
  } = props;
  const [purpose, setPurpose] = useState<FileIntakePurpose>(defaultPurpose);
  const [instruction, setInstruction] = useState('');
  const [site, setSite] = useState('');
  const [autoActionMode, setAutoActionMode] = useState<FileIntakeActionMode>(defaultActionMode);
  const [autoIndexKnowledge, setAutoIndexKnowledge] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPurpose(defaultPurpose);
    setInstruction('');
    setSite('');
    setAutoActionMode(defaultActionMode);
    setAutoIndexKnowledge(false);
  }, [defaultActionMode, defaultPurpose, open]);

  const selectedPurpose = useMemo(
    () => FILE_INTAKE_PURPOSE_OPTIONS.find((option) => option.value === purpose),
    [purpose],
  );
  const selectedActionMode = useMemo(
    () => FILE_INTAKE_ACTION_MODE_OPTIONS.find((option) => option.value === autoActionMode),
    [autoActionMode],
  );
  const siteRequired = purposeRequiresSite(purpose, autoActionMode);
  const canSubmit = !loading && (!siteRequired || Boolean(site.trim()));

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next && !loading) onCancel(); }}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          <div className="space-y-2">
            <Label>Selected Files</Label>
            <div className="flex flex-wrap gap-2">
              {files.map((file) => (
                <Badge key={`${file.name}-${file.size}`} variant="outline">{file.name}</Badge>
              ))}
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label>Purpose</Label>
              <Select value={purpose} onValueChange={(value) => setPurpose(value as FileIntakePurpose)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select purpose" />
                </SelectTrigger>
                <SelectContent>
                  {FILE_INTAKE_PURPOSE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="text-xs text-muted-foreground">
                {selectedPurpose?.description}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Action Mode</Label>
              <Select value={autoActionMode} onValueChange={(value) => setAutoActionMode(value as FileIntakeActionMode)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select action mode" />
                </SelectTrigger>
                <SelectContent>
                  {FILE_INTAKE_ACTION_MODE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="text-xs text-muted-foreground">
                {selectedActionMode?.description}
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Instruction</Label>
            <Textarea
              rows={4}
              value={instruction}
              onChange={(event) => setInstruction(event.target.value)}
              placeholder="Example: summarize this workshop report and prepare an incident draft for the delay at Jurong Truck Centre."
            />
            <div className="text-xs text-muted-foreground">
              Use one sentence to tell the system why you uploaded this file.
            </div>
          </div>

          <div className="space-y-2">
            <Label>{siteRequired ? 'Site / Branch (Required)' : 'Site / Branch (Optional)'}</Label>
            <Input
              value={site}
              onChange={(event) => setSite(event.target.value)}
              placeholder="Example: Jurong Truck Centre"
            />
            <div className="text-xs text-muted-foreground">
              Required when the system should create a live draft record immediately.
            </div>
          </div>

          <div className="flex items-start gap-3 rounded-lg border bg-muted/10 px-4 py-3">
            <Checkbox
              id="auto-index-knowledge"
              checked={autoIndexKnowledge}
              onCheckedChange={(checked) => setAutoIndexKnowledge(Boolean(checked))}
            />
            <div className="space-y-1">
              <Label htmlFor="auto-index-knowledge">Also index this file into document knowledge</Label>
              <div className="text-xs text-muted-foreground">
                When enabled, the system will try to push this file into document RAG so it can be searched and reused later.
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={() => onConfirm({ purpose, instruction, site, autoActionMode, autoIndexKnowledge })}
            disabled={!canSubmit}
          >
            {loading ? 'Processing...' : 'Continue'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
